"use client";

import { useEffect, useRef } from "react";
import { type Lang } from "@/lib/i18n";
import { readConsent } from "@/lib/analytics/consent";
import { initializeAssistantAnalytics } from "@/lib/analytics/assistant-funnel";
import { ConsentBanner } from "./ConsentBanner";
import { PostHogPageView } from "./PostHogPageView";
import { AssistantFunnel } from "./AssistantFunnel";
import { GoogleAnalytics } from "./GoogleAnalytics";

/**
 * App-wide analytics mount, rendered from the `[lang]` layout (so it has the
 * locale for the consent banner). Two providers, each on only when the portal
 * record names it (decisions.md D12), behind the ONE consent banner: PostHog
 * (`posthogId`) only ever *captures* after explicit consent —
 * `opt_out_capturing_by_default` + memory persistence means no network calls
 * and no cookies until the visitor accepts — and GA4 (`gaId`) is not even
 * requested before it (GoogleAnalytics.tsx). Renders nothing when neither is
 * configured for the portal.
 */
export function Analytics({
  lang,
  posthogId,
  gaId,
  host,
}: {
  lang: Lang;
  posthogId: string | null;
  /** A validated GA4 measurement ID (lib/analytics/ga.ts), or null. */
  gaId: string | null;
  host: string;
}) {
  const started = useRef(false);

  useEffect(() => {
    if (!posthogId || started.current) return;
    started.current = true;
    const consent = readConsent();
    // Lazy-load posthog-js so its ~50KB stays out of every page's first-load
    // bundle — it's fetched only after mount, and only when the portal has a
    // posthogId. The dynamic import returns the same module singleton the page
    // tracker + consent handler use (ESM module cache).
    void import("posthog-js").then(({ default: posthog }) => {
      posthog.init(posthogId, {
        api_host: host,
        loaded: (client) =>
          initializeAssistantAnalytics((event, properties) => client.capture(event, properties)),
        capture_pageview: false, // captured manually — App Router has no full reload
        capture_pageleave: true,
        autocapture: true,
        persistence: consent === "granted" ? "localStorage+cookie" : "memory",
        opt_out_capturing_by_default: consent !== "granted",
      });
    });
  }, [posthogId, host]);

  if (!posthogId && !gaId) return null;
  return (
    <>
      {posthogId ? (
        <>
          <PostHogPageView />
          <AssistantFunnel />
        </>
      ) : null}
      {gaId ? <GoogleAnalytics measurementId={gaId} /> : null}
      <ConsentBanner lang={lang} />
    </>
  );
}
